import { useState } from "react";
import {
  Scale, AlertTriangle, CheckCircle2, XCircle, HelpCircle,
  Layers, GitCompareArrows, ListChecks, Gavel, BookOpen, ShieldAlert,
  Crosshair, FileWarning, FileDown, Link2, ExternalLink, ShieldCheck,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Pill = ({ v }) => {
  if (!v) return null;
  const key = String(v).toLowerCase().replace(/\s+/g, "_");
  return <span className={`pill p-${key}`} data-testid={`pill-${key}`}>{String(v).replace(/_/g, " ")}</span>;
};

const SectionHead = ({ n, title, sub, icon }) => (
  <div className="fl-sh">
    <span className="n">{n}</span>
    {icon}
    <h3>{title}</h3>
    {sub && <span className="sub">{sub}</span>}
  </div>
);

const sourceLink = (src) => {
  if (!src) return null;
  const s = String(src);
  const cite = s.match(/\[\d{4}\]\s+[A-Za-z][A-Za-z.]*(?:\s+[A-Za-z.()]+)*\s+\d+/);
  if (cite) return `https://www.bailii.org/cgi-bin/find_by_citation.cgi?citation=${encodeURIComponent(cite[0])}`;
  if (/\bact\b/i.test(s)) return `https://www.legislation.gov.uk/all?text=${encodeURIComponent(s)}`;
  return `https://www.google.com/search?q=${encodeURIComponent(s)}`;
};

const verdictIcon = (a) => {
  if (a === "sound") return <CheckCircle2 size={26} color="#8fd0a3" />;
  if (a === "challengeable") return <ShieldAlert size={26} color="#e79b95" />;
  return <AlertTriangle size={26} color="#e6c583" />;
};
const appliedIcon = (v) => {
  if (v === "yes") return <CheckCircle2 size={15} color="#6bbd86" />;
  if (v === "no") return <XCircle size={15} color="#e07b74" />;
  if (v === "partial") return <AlertTriangle size={15} color="#e6c583" />;
  return <HelpCircle size={15} color="#9aa1a9" />;
};

const arr = (x) => (Array.isArray(x) ? x : []);

export const ResultView = ({ result, sectorLabel, analysisId, firm, toolbarExtra }) => {
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const r = result || {};
  const fullAudit = arr(r.citation_audit);
  const auditByOriginal = Object.fromEntries(fullAudit.map((a) => [a.original, a]));
  const audit = fullAudit.filter((a) => !verifiedOnly || a.status === "verified");
  const trace = arr(r.reasoning_trace).filter((t) => {
    if (!verifiedOnly) return true;
    const a = auditByOriginal[(t.source || "").trim()];
    return a && a.status === "verified";
  });
  const pdfUrl = analysisId ? `${API}/analyses/${analysisId}/pdf${verifiedOnly ? "?verified_only=true" : ""}` : null;

  return (
    <div className="fl-results" data-testid="results-panel">
      <div className="fl-toolbar no-print">
        {sectorLabel && <span className="fl-chip" data-testid="sector-chip">{sectorLabel}</span>}
        <button className={`fl-toggle ${verifiedOnly ? "on" : ""}`} data-testid="verified-only-toggle"
                onClick={() => setVerifiedOnly((v) => !v)}>
          <ShieldCheck size={15} /> Verified sources only
        </button>
        <div style={{ flex: 1 }} />
        {toolbarExtra}
        {pdfUrl ? (
          <a className="fl-btn fl-btn--ghost fl-btn--sm" data-testid="download-pdf-btn"
             href={pdfUrl} target="_blank" rel="noopener noreferrer">
            <FileDown size={16} /> Download PDF{verifiedOnly ? " (verified)" : ""}
          </a>
        ) : (
          <button className="fl-btn fl-btn--ghost fl-btn--sm" data-testid="download-pdf-btn" disabled>
            <FileDown size={16} /> Download PDF
          </button>
        )}
      </div>

      <div className={`fl-verdict fl-v-${r.overall?.assessment || "mixed"}`} data-testid="verdict-banner">
        <div className="fl-v-icon" style={{ background: "#12161c" }}>{verdictIcon(r.overall?.assessment)}</div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <h2>{(r.overall?.assessment || "mixed").replace(/_/g, " ")} <Pill v={r.overall?.confidence} /></h2>
          <p>{r.overall?.reasoning}</p>
        </div>
      </div>
      {r.summary && <div className="fl-note" data-testid="summary-note"><b style={{ color: "#cfe0c4" }}>Summary — </b>{r.summary}</div>}

      <div className="fl-section">
        <SectionHead n="01" title="Element-by-element accuracy" sub="did each legal ingredient apply?" icon={<Layers size={18} color="#c9a24b" />} />
        {arr(r.elements).length ? (
          <div className="fl-grid">
            {arr(r.elements).map((el, i) => (
              <div className="fl-item" key={i} data-testid={`element-${i}`}>
                <h4><span style={{ display: "flex", gap: 7, alignItems: "center" }}>{appliedIcon(el.applied)}{el.name}</span><Pill v={el.applied} /></h4>
                <div className="req">{el.required_test}</div>
                <div className="an">{el.analysis} <span className="conf">confidence: {el.confidence}</span></div>
              </div>
            ))}
          </div>
        ) : <div className="fl-none" data-testid="elements-empty">No discrete legal elements were isolated for this text — it may be a discretionary or narrative decision rather than an element-based one.</div>}
      </div>

      <div className="fl-section">
        <SectionHead n="02" title="The sting · the defence · the counter" icon={<Crosshair size={18} color="#c9a24b" />} />
        <div className="fl-sdc">
          <div className="box"><div className="lab">The sting</div><p data-testid="sdc-sting">{r.sting_defence_counter?.sting || "—"}</p></div>
          <div className="box"><div className="lab">The defence</div><p data-testid="sdc-defence">{r.sting_defence_counter?.defence || "—"}</p></div>
          <div className="box"><div className="lab">Counter-claim</div><p data-testid="sdc-counter">{r.sting_defence_counter?.counterclaim || "None identified"}</p></div>
        </div>
      </div>

      <div className="fl-section">
        <SectionHead n="03" title="Right parties & right legislation" icon={<Scale size={18} color="#c9a24b" />} />
        <div className="fl-grid">
          <div className="fl-item"><h4>Correct defendants?</h4><div className="an" data-testid="parties-defendants">{r.parties_law?.right_defendants || "—"}</div></div>
          <div className="fl-item"><h4>Correct legislation used?</h4><div className="an" data-testid="parties-legislation">{r.parties_law?.right_legislation || "—"}</div></div>
          <div className="fl-item"><h4>Issues</h4><div className="an" data-testid="parties-issues">{r.parties_law?.issues || "None identified"}</div></div>
        </div>
      </div>

      <div className="fl-section">
        <SectionHead n="04" title="Internal contradictions" sub="word-forensics" icon={<GitCompareArrows size={18} color="#c9a24b" />} />
        {arr(r.contradictions).length ? arr(r.contradictions).map((c, i) => (
          <div className="fl-contra" key={i} data-testid={`contradiction-${i}`}>
            <div className="qq">“{c.quote_a}”</div>
            <div className="vs">⟺ conflicts with · <Pill v={c.severity} /></div>
            <div className="qq">“{c.quote_b}”</div>
            <div className="why">{c.why}</div>
          </div>
        )) : <div className="fl-none good" data-testid="contradictions-empty">No internal contradictions detected — a point in the decision's favour.</div>}
      </div>

      <div className="fl-section">
        <SectionHead n="05" title="Unresolved — falling through the cracks" icon={<FileWarning size={18} color="#c9a24b" />} />
        {arr(r.unresolved).length ? (
          <div className="fl-grid">
            {arr(r.unresolved).map((u, i) => (
              <div className="fl-item" key={i} data-testid={`unresolved-${i}`}>
                <h4>{u.issue}<Pill v={u.confidence} /></h4>
                <div className="an">{u.why_it_matters}</div>
              </div>
            ))}
          </div>
        ) : <div className="fl-none good" data-testid="unresolved-empty">No material issue appears to have been left undetermined.</div>}
      </div>

      <div className="fl-section">
        <SectionHead n="06" title="Was the correct test applied?" icon={<ListChecks size={18} color="#c9a24b" />} />
        {arr(r.test_scope).length ? (
          <div className="fl-grid">
            {arr(r.test_scope).map((t, i) => (
              <div className="fl-item" key={i} data-testid={`testscope-${i}`}>
                <h4>{t.test}<Pill v={t.verdict} /></h4>
                <div className="an">{t.explanation}</div>
              </div>
            ))}
          </div>
        ) : <div className="fl-none" data-testid="testscope-empty">No distinct legal test was identified for scope-checking.</div>}
      </div>

      <div className="fl-section">
        <SectionHead n="07" title="Comparative authorities" sub="cases · jurisdictions · sectors" icon={<BookOpen size={18} color="#c9a24b" />} />
        {arr(r.comparators).length ? (
          <div className="fl-grid">
            {arr(r.comparators).map((c, i) => (
              <div className="fl-item" key={i} data-testid={`comparator-${i}`}>
                <h4>{c.authority}</h4>
                <div className="req">{c.relevance}</div>
                <div className="an">{c.note}</div>
              </div>
            ))}
          </div>
        ) : <div className="fl-none" data-testid="comparators-empty">No comparable authorities surfaced from the text alone.</div>}
      </div>

      <div className="fl-section">
        <SectionHead n="08" title="Options — ranked" sub="or reasoned confirmation" icon={<Gavel size={18} color="#c9a24b" />} />
        {arr(r.options).length ? arr(r.options).map((o, i) => (
          <div className="fl-opt" key={i} data-testid={`option-${i}`}>
            <div className="fl-v-icon" style={{ width: 40, height: 40, background: "#12161c" }}><Gavel size={18} color="#c9a24b" /></div>
            <div>
              <h4>{o.route} <Pill v={o.strength} /> {o.time_sensitive && <span className="clock">time-sensitive</span>}</h4>
              <div className="aim">Aim: {o.aim}</div>
              <div className="why">{o.why}</div>
            </div>
          </div>
        )) : <div className="fl-none" data-testid="options-empty">No actionable options generated.</div>}
      </div>

      <div className="fl-section">
        <SectionHead n="09" title="Reasoning trace" sub="every conclusion → its source in law" icon={<Link2 size={18} color="#c9a24b" />} />
        {trace.length ? (
          <div className="fl-trace">
            {trace.map((t, i) => {
              const a = auditByOriginal[(t.source || "").trim()];
              const href = a?.url || sourceLink(t.source);
              const status = a?.status || null;
              return (
                <div className="fl-tracerow" key={i} data-testid={`trace-${i}`}>
                  <div className="tr-src">
                    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={`trace-source-link-${i}`}>
                      {t.source} <ExternalLink size={11} className="tr-ext" />
                    </a>
                    {status && <span className={`tr-badge tb-${status}`} data-testid={`trace-status-${i}`}>{status}</span>}
                    {status && status !== "verified" && (
                      <div className="tr-pending" data-testid={`trace-pending-${i}`}>Verification pending — confirm before reliance</div>
                    )}
                  </div>
                  <div className="tr-body"><b>{t.conclusion}</b><span>{t.basis}</span></div>
                </div>
              );
            })}
          </div>
        ) : <div className="fl-none" data-testid="trace-empty">{verifiedOnly ? "No live-verified sources — switch off “Verified sources only” to see all model-assisted citations." : "No source-linked reasoning trace was produced for this analysis."}</div>}
      </div>

      <div className="fl-section">
        <SectionHead n="10" title="Citation audit" sub="live-checked against legislation.gov.uk / BAILII" icon={<ShieldCheck size={18} color="#c9a24b" />} />
        {audit.length ? (
          <div className="fl-caudit">
            {audit.map((c, i) => (
              <div className="fl-cauditrow" key={i} data-testid={`citation-${i}`}>
                <div className="ca-main">
                  <a href={c.url} target="_blank" rel="noopener noreferrer" data-testid={`citation-link-${i}`}>{c.citation} <ExternalLink size={11} className="tr-ext" /></a>
                  <div className="ca-note">{c.note}</div>
                </div>
                <span className={`pill p-${(c.status || "").toLowerCase()}`} data-testid={`citation-status-${i}`}>{c.status}</span>
              </div>
            ))}
          </div>
        ) : <div className="fl-none" data-testid="citation-empty">{verifiedOnly ? "No citations were live-verified for this analysis." : "No citations were extracted for verification."}</div>}
      </div>

      <div className="fl-disc" data-testid="disclaimer">
        {firm && <><b style={{ color: "#cbc4b4" }}>Prepared by {firm}</b> · </>}<b style={{ color: "#c9a24b" }}>lexgap.app</b> — LexGap produces analytical
        assessments, not legal advice, and no lawyer–client relationship is created. “Verified” means a statute URL resolved
        on legislation.gov.uk; other citations are marked model-verified (confirm on BAILII). Verify against sealed primary
        documents before acting.
      </div>
    </div>
  );
};

export default ResultView;
